import { autoUpdate, arrow, computePosition, ComputePositionReturn, flip, offset, ReferenceElement, shift, limitShift } from '@floating-ui/dom';
import { Component, ComponentInterface, Element, Host, JSX, Listen, Prop, Watch, h, readTask } from '@stencil/core';
import { TooltipPosition, TooltipVariant } from '../../../components/overlays/tooltip/tooltip.types';
import { transformTagNames } from '../../../global/transform-tag-name';
import { Keys } from '../../../shared/keys';
import { getRandomId } from '../../../utils/get-random-id';
import { isTouchDevice } from '../../../utils/is-touch-device';
import { CURSOR_VARIANT, TOP_POSITION } from './tooltip.constants';

/**
 * Tooltips provide the user with object-related information.
 *
 * The Tooltip is rendered inside an overlay container to ensure it appears above other content and is not clipped by parent elements. This overlay container manages positioning and stacking context for the Tooltip. For instance, if the Tooltip is rendered within a modal, an element within the modal should be passed as the container using the `overlayContainer` prop.
 *
 * @slot - Element that triggers the tooltip like a button or anchor
 * @slot content - Content to be displayed in the tooltip overlay
 */
@Component({
  tag: 'wb-tooltip',
  styleUrl: './tooltip.scss',
  shadow: true,
})
export class TooltipComponent implements ComponentInterface {
  private uniqueId = `wb-tooltip-overlay-${getRandomId()}`;

  @Element()
  public el: HTMLElement;

  /** Optional tooltip variant (inline display property to use inside other webcomponents, if styling is broken) */
  @Prop({ reflect: true })
  public variant?: TooltipVariant;

  /** If present, shows overlay */
  @Prop({ reflect: true, mutable: true })
  public open: boolean = false;

  /** Position of the tooltip */
  @Prop({ reflect: true })
  public position: TooltipPosition = TOP_POSITION;

  /** If present, won't trigger tooltip content */
  @Prop({ reflect: true, mutable: true })
  public disabled?: boolean = false;

  /** If present, the tooltip contents will be interactable */
  @Prop({ reflect: true })
  public interactable?: boolean = false;

  /** If present, will append tooltip's overlay to the container provided */
  @Prop() overlayContainer?: HTMLElement = document.body;

  @Watch('position')
  public onConfigurationChange(): void {
    this.updatePosition();
  }

  @Watch('open')
  public onOpenChange(): void {
    this.open && !this.disabled ? this.openOverlay(this.isTouchDevice) : this.closeOverlay();
  }

  @Watch('disabled')
  public onDisableChange(): void {
    if (this.disabled && this.open) {
      this.closeOverlay();
    }
  }

  @Listen('keydown', { target: 'window' })
  public onKeyDown(e: KeyboardEvent): void {
    if (this.open && e.key === Keys.ESC) {
      this.closeOverlay();
    }
  }

  private TagNames = transformTagNames(this, 'wb-tooltip', ['wb-text']);

  private overlayEl: HTMLElement;
  private triggerEl: HTMLElement;
  private virtualEl: ReferenceElement;
  private arrowEl: HTMLElement;
  private safePolygonEl: HTMLElement;
  private isTouchDevice: boolean;

  private cleanup: () => void;

  public connectedCallback(): void {
    this.isTouchDevice = isTouchDevice();
  }

  public componentDidLoad(): void {
    if (this.open && !this.disabled) {
      // wait for the next tick to make sure the triggerEl is set
      readTask(() => this.openOverlay(false));
    }
  }

  public disconnectedCallback(): void {
    this.removeCloseOnClickOutsideListener();
    this.closeOverlay();
  }

  private updatePosition = async (): Promise<void> => {
    const computeElPosition = this.variant === CURSOR_VARIANT ? this.computeVirtualElPosition : this.computeTriggerElPosition;
    const { x, y, placement, middlewareData } = await computeElPosition();

    Object.assign(this.overlayEl.style, {
      left: `${x}px`,
      top: `${y}px`,
    });

    this.overlayEl.classList.forEach((className) => {
      // eslint-disable-next-line workbench/tag-name-transform
      if (className.startsWith('wb-tooltip--position-')) {
        this.overlayEl.classList.remove(className);
      }
    });
    this.overlayEl.classList.add(`wb-tooltip--position-${placement}`);

    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[placement.split('-')[0]];

    if (this.interactable) {
      const SAFE_PADDING_SIDE = '10px';

      if (staticSide === 'top' || staticSide === 'bottom') {
        Object.assign(this.safePolygonEl.style, {
          left: '0',
          right: '',
          top: '',
          bottom: '',
          [staticSide]: `-${SAFE_PADDING_SIDE}`,
          height: SAFE_PADDING_SIDE,
          width: `${this.overlayEl.clientWidth}px`,
        });
      } else {
        Object.assign(this.safePolygonEl.style, {
          left: '',
          right: '',
          top: '0',
          bottom: '',
          [staticSide]: `-${SAFE_PADDING_SIDE}`,
          height: `${this.overlayEl.clientHeight}px`,
          width: SAFE_PADDING_SIDE,
        });
      }
    }

    if (middlewareData.arrow) {
      Object.assign(this.arrowEl.style, {
        display: undefined,
        left: middlewareData.arrow.x ? `${middlewareData.arrow.x}px` : '',
        top: middlewareData.arrow.y ? `${middlewareData.arrow.y}px` : '',
        right: '',
        bottom: '',
        [staticSide]: '-4px',
      });
    } else {
      this.arrowEl.style.display = 'none';
    }
  };

  private computeVirtualElPosition = (): Promise<ComputePositionReturn> => {
    let preferedPlacement;
    let crossAxis = 12;

    switch (this.position) {
      case 'end':
        preferedPlacement = 'bottom-end';
        crossAxis = -crossAxis;
        break;
      default:
        preferedPlacement = 'bottom-start';
    }

    return computePosition(this.virtualEl, this.overlayEl, {
      placement: preferedPlacement,
      middleware: [
        offset({
          mainAxis: 24,
          crossAxis,
        }),
      ],
    });
  };

  private computeTriggerElPosition = (): Promise<ComputePositionReturn> => {
    let preferedPlacement;

    switch (this.position) {
      case 'top':
        preferedPlacement = 'top';
        break;
      case 'bottom':
        preferedPlacement = 'bottom';
        break;
      case 'start':
        preferedPlacement = 'left';
        break;
      case 'end':
        preferedPlacement = 'right';
        break;
    }

    return computePosition(this.triggerEl, this.overlayEl, {
      placement: preferedPlacement,
      strategy: 'absolute',
      middleware: [
        offset(8), //
        flip({
          padding: 16,
          fallbackAxisSideDirection: 'start',
        }),
        shift({ padding: 16, limiter: limitShift() }),
        arrow({ element: this.arrowEl, padding: 8 }),
      ],
    });
  };

  private openOverlay = (closeOnClickOutside: boolean): void => {
    if (document.getElementById(this.uniqueId)) {
      return; // make sure overlay is only attached once
    }

    if (closeOnClickOutside) {
      this.addCloseOnClickOutsideListener();
    }

    this.createOverlayEl();
    if (this.overlayEl) {
      this.overlayContainer.appendChild(this.overlayEl);
      this.updatePosition();
    }
  };

  private closeOverlay = (): void => {
    if (this.isTouchDevice) {
      this.removeCloseOnClickOutsideListener();
    }

    if (this.overlayContainer.contains(this.overlayEl)) {
      this.overlayEl?.remove();
      this.arrowEl?.remove();
      this.cleanup();
    }
  };

  private addCloseOnClickOutsideListener = (): void => {
    window.addEventListener('touchstart', this.closeIfClickOutside);
  };

  private removeCloseOnClickOutsideListener = (): void => {
    window.removeEventListener('touchstart', this.closeIfClickOutside);
  };

  private closeIfClickOutside = (evt: Event): void => {
    const path = evt.composedPath();
    const isClickInside = path.includes(this.el);

    if (!isClickInside) {
      this.closeOverlay();
    }
  };

  private updateTriggerEl = (evt: Event): void => {
    const slottedElements = (evt.target as HTMLSlotElement).assignedElements();

    if (slottedElements.length !== 1) {
      console.warn(`wb-tooltip: exactly one trigger element must be slotted, got ${slottedElements.length}`, evt.target);
      if (!slottedElements.length) {
        return;
      }
    }

    this.triggerEl = slottedElements[0] as HTMLElement;

    this.triggerEl.addEventListener('mouseenter', () => (this.open = true));
    this.triggerEl.addEventListener('mouseleave', (e: MouseEvent) => {
      if (!this.interactable || !this.overlayEl?.contains(e.relatedTarget as HTMLElement)) {
        this.open = false;
      }
    });
    this.triggerEl.addEventListener('focus', () => (this.open = true));
    this.triggerEl.addEventListener('blur', () => (this.open = false));

    if (this.variant === CURSOR_VARIANT) {
      this.triggerEl.addEventListener('mousemove', this.setVirtualEl);
    }
  };

  private createOverlayEl() {
    if (!this.hasSlotContent() && !this.hasContent()) {
      return;
    }

    const tooltipEl = document.createElement('div');
    const arrowEl = document.createElement('div');

    tooltipEl.id = this.uniqueId;

    // eslint-disable-next-line workbench/tag-name-transform
    tooltipEl.classList.add('wb-tooltip-overlay');
    const tooltipElStyle = getComputedStyle(this.el);

    Object.assign(tooltipEl.style, {
      borderRadius: tooltipElStyle.getPropertyValue('--tooltip-border-radius'),
      backgroundColor: tooltipElStyle.getPropertyValue('--tooltip-background-color'),
      color: tooltipElStyle.getPropertyValue('--tooltip-text-color'),
      padding: `${tooltipElStyle.getPropertyValue('--tooltip-padding-top')} ${tooltipElStyle.getPropertyValue('--tooltip-padding-right')} ${tooltipElStyle.getPropertyValue('--tooltip-padding-bottom')} ${tooltipElStyle.getPropertyValue('--tooltip-padding-left')}`,
      maxWidth: tooltipElStyle.getPropertyValue('--tooltip-max-width'),
      position: 'absolute',
      zIndex: tooltipElStyle.getPropertyValue('--z-index'),
      boxSizing: 'border-box',
      pointerEvents: this.interactable ? 'auto' : 'none',
      width: 'max-content',
      top: '0', // add top and left value initially to prevent scroll bar flickering (https://git.i.mercedes-benz.com/dh-io-workbench/core/issues/3548)
      left: '0',
    });

    // eslint-disable-next-line workbench/tag-name-transform
    arrowEl.classList.add('wb-tooltip-overlay-arrow');
    Object.assign(arrowEl.style, {
      position: 'absolute',
      background: tooltipElStyle.getPropertyValue('--tooltip-background-color'),
      width: '8px',
      height: '8px',
      transform: 'rotate(45deg)',
    });

    const overlayTextEl = document.createElement(this.TagNames.WbText) as HTMLWbTextElement;
    overlayTextEl.size = 'm';
    overlayTextEl.static = true;

    const contentSlotClone = this.hasSlotContent()?.cloneNode(true) as HTMLElement;
    contentSlotClone?.removeAttribute('slot');
    overlayTextEl?.appendChild(contentSlotClone);

    tooltipEl?.appendChild(overlayTextEl);
    tooltipEl?.append(arrowEl);

    this.overlayEl = tooltipEl;
    this.arrowEl = arrowEl;

    if (this.interactable) {
      const safePolygonEl = document.createElement('div');

      // eslint-disable-next-line workbench/tag-name-transform
      safePolygonEl.classList.add('wb-tooltip-overlay-safe-polygon');
      Object.assign(safePolygonEl.style, {
        position: 'absolute',
        width: '0',
        height: '0',
        top: '0',
        left: '0',
      });

      tooltipEl?.append(safePolygonEl);

      this.safePolygonEl = safePolygonEl;

      tooltipEl.addEventListener('mouseleave', (e: MouseEvent) => {
        if (e.relatedTarget !== this.triggerEl) {
          this.open = false;
        }
      });
    }

    this.cleanup = autoUpdate(this.triggerEl, this.overlayEl, this.updatePosition);
  }

  private setVirtualEl = ({ clientX, clientY }) => {
    this.virtualEl = {
      getBoundingClientRect() {
        return {
          width: 0,
          height: 0,
          x: clientX,
          y: clientY,
          left: clientX,
          right: clientX,
          top: clientY,
          bottom: clientY,
        };
      },
    };

    this.updatePosition();
  };

  private hasSlotContent = (): HTMLElement | null => {
    return this.el.querySelector('[slot="content"]');
  };

  private getSlotContent = (): string => {
    return this.hasSlotContent()?.innerText.trim() ?? '';
  };

  private hasContent = (): boolean => {
    return !!this.getSlotContent();
  };

  public render(): JSX.Element {
    return (
      <Host class="wb-tooltip" role="tooltip" aria-label={this.getSlotContent()}>
        <div class="wb-tooltip__container" data-testid="wb-tooltip__container">
          <slot onSlotchange={this.updateTriggerEl} />
        </div>
      </Host>
    );
  }
}
